<template>
  <div class="page-container">
    <MainTitle 
      class="main-title-container"
      title="Profile" 
    />
    <div class="app-content-container user-profile">
      <Card class="p-card user-info">
        <template #content>
          <p class="sections">
            User Settings
          </p>
          <Avatar
            icon="pi pi-user"
            class="profile-photo"
            size="xlarge"
            shape="circle"
          />
          <p class="titles">
            {{ user.first_name + " " + user.last_name }}
          </p>
          <p class="subtitles">
            Active since 2025
          </p>
          <div class="input-group">
            <label 
              class="label" 
              for="username"
            >
              Email 
            </label>
            <InputText
              id="user.email"
              v-model="user.email"
              class="info"
              aria-describedby="username-help"
            />
          </div>
        </template>
      </Card>
      <Card 
        class="p-card activity-info" 
        data-test="statistics-section"
      >
        <template #title />
        <template #content>
          <p class="sections">
            Your Week In Numbers
          </p>
          <div class="section-cards">
            <Card class="insights-new-vocabulary">
              <template #title>
                <p class="new-vocabulary-title">
                  Newly Added Vocabulary
                </p>
              </template>
              <template #content>
                <p class="new-vocabulary-info">
                  {{ nbNewVocabulary }} words
                </p>
              </template>
            </Card>
            <LearningInsights
              class="learning-insights"
              :new-voc-percentage="newVocPercentage"
              :reviewed-voc-percentage="reviewedVocPercentage"
              :remembered-voc-percentage="rememberedVocPercentage"
            />
          </div>
          <div class="section-calendar">
            <Card class="calendar-card">
              <template #title>
                <p class="small-title">
                  Number of words reviewed
                </p>
              </template>
              <template #content>
                <ActivityHeatmap :activities="activity" />
              </template>
            </Card>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
  import { Card, Avatar, InputText } from "primevue";

  import MainTitle from "@/components/title/MainTitle.vue";
  import LearningInsights from "@/components/userprofile/LearningInsights.vue";
  import ActivityHeatmap from "@/components/userprofile/ActivityHeatmap.vue";

  import {
    REVIEW_INSIGHTS_ENDPOINT,
    USER_PROFILE_ENDPOINT,
    BASE_URL,
  } from "../../assets/constants/api_endpoints.js";
  import { getWithAuthentication } from "@/services/api";

  export default {
    components: {
      Card,
      MainTitle,
      Avatar,
      InputText,
      LearningInsights,
      ActivityHeatmap,
    },
    data() {
      return {
        user: {
          first_name: "",
          last_name: "",
          email: "",
        },
        activity: [],
        newVocPercentage: 0,
        reviewedVocPercentage: 0,
        rememberedVocPercentage: 0,
        nbNewVocabulary: 0,
      };
    },
    async mounted() {
      await this.getUserProfile();
      await this.getUserPreviewInsights();
    },
    methods: {
      async getUserProfile() {
        try {
          const response = await getWithAuthentication(
            BASE_URL,
            USER_PROFILE_ENDPOINT
          );
          if (response && response.status === 200) {
            let data = response.data;
            this.user = data;
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          this.$router.push({ name: "LoginForm", params: { loggedOut: true } });
        }
      },
      async getUserPreviewInsights() {
        try {
          const response = await getWithAuthentication(
            BASE_URL,
            REVIEW_INSIGHTS_ENDPOINT
          );
          if (response && response.status === 200) {
            let insights = response.data;

            if (insights.phase_percentage.length >= 3) {
              this.newVocPercentage = insights.phase_percentage[0];
              this.reviewedVocPercentage = insights.phase_percentage[1];
              this.rememberedVocPercentage = insights.phase_percentage[2];
            }

            if (insights.nb_new_vocabulary) {
              this.nbNewVocabulary = insights.nb_new_vocabulary;
            } else {
              this.nbNewVocabulary = 0;
            }

            if (insights.activity) {
              this.activity = [];
              let maxDays = 7;
              for (let i = 0; i < maxDays; i++) {
                this.activity.push({
                  date: new Date(Date.now() - i * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split("T")[0],
                  count: insights.activity[maxDays - i - 1],
                });
              }
            } else {
              this.activity = [];
            }
          }
        } catch (error) {
          console.error("Error fetching user preview insights:", error);
          this.$router.push({ name: "LoginForm", params: { loggedOut: true } });
        }
      },
    },
  };
</script>

<style scoped>
.app-content-container.user-profile {
    display: flex;
    flex-direction: row;
    gap: 20px;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    margin-top: 60px;
    padding-bottom: 50px;
    padding-left: var(--space-8);
    padding-right: var(--space-8);
}

.user-profile :deep(.p-card) {
    background: var(--color-surface) !important;
    box-shadow: none !important;
    border: 1px solid var(--color-primary) !important;
}

.user-profile :deep(.p-card-body),
.user-profile :deep(.p-card-content) {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
}

@media (max-width: 599px) {
  .app-content-container.user-profile {
    flex-direction: column;
    gap: 16px;
  }

  .app-content-container {
    left: 0;
    width: 100%;
  }

  .main-title-container :deep(.title-container) {
    position: absolute;
    right: auto !important;
  }

}

.p-card.user-info {
    flex: 1;
    min-width: min(100%, 400px);
    max-width: 500px;
    display: flex;
    border-radius: var(--radius-lg);
    min-height: 600px;
    height: auto;
    align-self: stretch;
    margin: 0 auto;
    margin-left: max(30px, 2vw);
}

@media (max-width: 599px) {
  .p-card.user-info {
    min-width: 100%;
    max-width: 100%;
    min-height: auto;
    margin-bottom: 16px;
    margin-left: 0;
    width: 100%;
    padding: 16px;
  }
}

.p-card.activity-info {
    flex: 2;
    min-width: min(100%, 600px);
    max-width: 1000px;
    display: flex;
    border-radius: var(--radius-lg);
    min-height: 600px;
    height: auto;
    align-self: stretch;
    margin: 0 auto;
}

@media (max-width: 599px) {
  .p-card.activity-info {
    min-width: 100%;
    max-width: 100%;
    min-height: auto;
    margin-bottom: 16px;
    margin-left: 0;
    width: 100%;
    padding: 16px;
    display: block;
  }
}

/* element.style {
}
<style>
@media (max-width: 599px) {
    .p-card.user-info[data-v-792291cd] {
        min-width: 100%;
        max-width: 100%;
        min-height: auto;
        margin-bottom: 16px;
        margin-left: 0;
        width: 100%;
        padding: 16px;
    } */

.section-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;
    width: 100%;
}

@media (max-width: 599px) {
  .section-cards {
    gap: 12px;
  }
}

.learning-insights {
    display: flex;
    flex: 1;
    min-width: 300px;
}

@media (max-width: 599px) {
  .learning-insights {
    width: 100%;
    overflow-x: auto;
  }
}

.main-info {
    width: clamp(80px, 15vw, 100px) !important;
}

.profile-photo {
    margin: 20px;
    width: clamp(80px, 15vw, 100px);
    height: clamp(80px, 15vw, 100px);
    margin-left: 85px;
}

@media (max-width: 599px) {
  .profile-photo {
    margin: 15px;
    width: clamp(60px, 12vw, 80px);
    height: clamp(60px, 12vw, 80px);
    margin-left: 70px;
  }
}

.label {
    display: inline-block;
}

@media (max-width: 599px) {
  .label {
    display: block;
    min-width: auto;
    font-size: 14px;
    margin-bottom: 4px;
  }
}

.input-group {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0;
    height: auto;
}

@media (max-width: 599px) {
  .input-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin: 12px 0;
  }
}

.info {
    display: inline-block;
    width: clamp(150px, calc(100% - 140px), 200px);
    flex: 1;
}

@media (max-width: 599px) {
  .info {
    display: block;
    width: 100%;
    margin-top: 0;
    font-size: 14px;
  }
}

.title {
    min-height: 50px;
    height: auto;
}

.small-title {
    font-size: var(--font-size-base);
    margin: 20px;
    text-align: center;
    color: var(--color-text-secondary);
    font-family: var(--font-family);
}

@media (max-width: 599px) {
  .small-title {
    font-size: 13px;
    margin: 12px;
  }
}

.user-name {
    font-size: large;
}

.p-card {
    margin-bottom: 20px;
    transition: 0.3s;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    align-content: center;
}

@media (max-width: 599px) {
  .p-card {
    width: 100%;
    margin-bottom: 12px;
  }
}

.titles {
    font-family: var(--font-family);
    font-size: var(--font-size-lg);
}

@media (max-width: 599px) {
  .titles {
    font-size: 16px;
  }
}

.subtitles {
    font-family: var(--font-family);
    font-size: var(--font-size-base);
    color: var(--color-text-secondary);
    margin-bottom: var(--space-6);
}

@media (max-width: 599px) {
  .subtitles {
    font-size: 13px;
    margin-bottom: 30px;
  }
}

.sections {
    text-align: center;

    text-transform: uppercase;
    letter-spacing: 1px;

    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-template-rows: 16px 0;
    grid-gap: 22px;

    margin: 20px;
}

@media (max-width: 599px) {
  .sections {
    grid-gap: 12px;
    margin: 12px;
    font-size: 12px;
  }
}

.sections:after,
.sections:before {
    content: " ";
    display: block;
    border-bottom: 1px solid rgba(27,30,100,0.15);
}

.insights-new-vocabulary {
    min-height: 200px;
    width: clamp(160px, 30%, 180px);
    height: 200px;
    margin: 5px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px;
    background-color: var(--color-surface-raised);
    border-radius: var(--radius-md);
}

@media (max-width: 599px) {
  .insights-new-vocabulary {
    min-height: 150px;
    height: 180px;
    width: 100%;
    margin: 0;
    padding: 8px;
  }
}

.new-vocabulary-title {
    font-size: clamp(13px, 2.5vw, 15px);
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--color-text-primary);
    font-family: var(--font-family);
    line-height: 1.4;
}

@media (max-width: 599px) {
  .new-vocabulary-title {
    font-size: 12px;
    margin-top: 8px;
  }
}

.new-vocabulary-info {
    font-size: clamp(24px, 4vw, 30px);
    text-align: center;
    margin-top: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif;
}

@media (max-width: 599px) {
  .new-vocabulary-info {
    font-size: clamp(18px, 3vw, 24px);
    margin-top: 8px;
  }
}

.calendar-card {
    width: 100%;
    max-width: 800px;
    height: auto;
    min-height: 200px;
    margin: 0 auto;
}

@media (max-width: 599px) {
  .calendar-card {
    max-width: 100%;
    min-height: 150px;
  }
}
</style>
